import type {
  AdvancedPrompt,
  ContextComposition,
  ContextMessage,
  ContextRecipe,
  HistoryUnit,
  StructuredMessageContent,
  SystemFragment,
  TrailingMessage,
} from './types'

/**
 * The shared token estimate. Deliberately the same crude heuristic both context
 * builders have always used — every budget in the app is calibrated against it,
 * so replacing it would silently change how much history fits.
 */
export function estimateTokens(text = ''): number {
  return Math.ceil(text.length / 4)
}

export function estimateContentTokens(content: StructuredMessageContent): number {
  return estimateTokens(typeof content === 'string' ? content : JSON.stringify(content))
}

const byOrder = <T extends { order: number }>(a: T, b: T): number => a.order - b.order

/** Join fragments with each one's declared separator. */
export function joinSystemFragments(fragments: SystemFragment[]): string {
  return [...fragments].sort(byOrder).reduce((text, fragment, index) => (
    index === 0 ? fragment.text : `${text}${fragment.separator ?? '\n'}${fragment.text}`
  ), '')
}

/** The advanced prompt has one reserved position, independent of fragment order. */
export function composeSystemText(systemText: string, advancedPrompt?: AdvancedPrompt | null): string {
  const prompt = advancedPrompt?.text?.trim()
  if (!prompt) return systemText
  const prefix = `${advancedPrompt?.label ?? 'Advanced prompt:'}\n${prompt}`
  return systemText ? `${prefix}\n\n${systemText}` : prefix
}

/**
 * Fill the budget from the newest unit backwards, stopping at the first unit
 * that does not fit. Stopping (rather than skipping ahead to something smaller)
 * is deliberate: history must stay contiguous, or the model sees a transcript
 * with holes in it.
 */
export function packHistory(units: HistoryUnit[], budget: number): ContextMessage[] {
  const ordered = [...units].sort(byOrder)
  const packed: ContextMessage[] = []
  let used = 0

  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const unit = ordered[index]
    if (used + unit.tokens > budget) break
    packed.unshift(...unit.messages)
    used += unit.tokens
  }

  return packed
}

/**
 * Compose the prompt for one model invocation.
 *
 * The composer owns everything universal — collecting contributions, assembling
 * the system message, budgeting, packing history, and emitting the payload —
 * and knows nothing about what the sources mean.
 */
export function composeContext<TInput>(recipe: ContextRecipe<TInput>, input: TInput): ContextMessage[] {
  const systemFragments: SystemFragment[] = []
  const historyUnits: HistoryUnit[] = []
  const trailingMessages: TrailingMessage[] = []

  for (const source of recipe.sources) {
    const contribution = source.contribute(input)
    if (contribution.systemFragments) systemFragments.push(...contribution.systemFragments)
    if (contribution.historyUnits) historyUnits.push(...contribution.historyUnits)
    if (contribution.trailingMessages) trailingMessages.push(...contribution.trailingMessages)
  }

  const systemText = composeSystemText(joinSystemFragments(systemFragments), recipe.advancedPrompt?.(input))
  const trailing = [...trailingMessages].sort(byOrder)

  const composition: ContextComposition = {
    systemText,
    trailingMessages: trailing,
    estimate: estimateContentTokens,
  }

  const packed = packHistory(historyUnits, recipe.historyBudget(input, composition))
  const arranged = recipe.arrange
    ? recipe.arrange({ history: packed, trailing }, input)
    : { history: packed, trailing }

  return [
    { role: 'system', content: systemText },
    ...arranged.history,
    ...[...arranged.trailing].sort(byOrder).map((entry) => entry.message),
  ]
}

export default composeContext
